import React, { PureComponent, Fragment } from 'react';
import {
    string,
    number,
    bool,
    func,
    arrayOf,
    shape,
    objectOf,
} from 'prop-types';
import classnames from 'classnames';
import compose from 'lodash/flowRight';
import { productShape, baseUrlShape, userShape } from 'shapes';
import {
    getProductBanner,
    category as categoryUtils,
    isBannerRecommended,
} from 'utils';
import { WishlistButton, withGTM } from 'components';
import {
    withBaseUrls,
    withSales,
    withEnvironmentData,
    withFeatureFlags,
    withWishList,
    withTranscriber,
    withUser,
} from 'connectors';
import {
    Merchandizing,
    ProductCard,
    ProductCardContent,
    ProductCardPicture,
    ProRibbon,
} from './components';
import { generateProductUrl, getCurrentThumbnail } from './Product.utils';

import './Product.scss';

class Product extends PureComponent {
    static propTypes = {
        className: string,
        lazyloadImage: bool,
        product: productShape.isRequired,
        categoryLevel: string,
        categoryId: number,
        showIconWishlist: bool,
        isInWishList: bool,
        promoted: bool,
        openWishlistModal: func,
        experiences: arrayOf(string),
        baseUrls: baseUrlShape.isRequired,
        isSalePeriod: bool.isRequired,
        cdn: shape({
            CDN_IMAGE: string,
        }).isRequired,
        platform: shape({
            platform: string,
        }).isRequired,
        hasSearchEngine: bool.isRequired,
        hasB2b: bool.isRequired,
        hasB2bCardStyle: bool.isRequired,
        hasB2bProRibbon: bool.isRequired,
        translations: objectOf(string).isRequired,
        pushToGTM: func.isRequired,
        dataPosition: number,
        user: userShape.isRequired,
    };

    static defaultProps = {
        className: '',
        lazyloadImage: false,
        categoryLevel: null,
        categoryId: null,
        showIconWishlist: false,
        isInWishList: false,
        promoted: false,
        openWishlistModal: () => {},
        experiences: [],
        dataPosition: null,
    };

    state = {
        currentImageIndex: 0,
        isSwipeActive: false,
    };

    showPreviousImage = event => {
        const originalEvent = event.event || event;
        originalEvent.preventDefault();
        let currentImageIndex = this.state.currentImageIndex;
        currentImageIndex--;

        if (currentImageIndex < 0) {
            currentImageIndex = this.props.product.thumbnails.length - 1;
        }
        this.setState({
            currentImageIndex,
            isSwipeActive: true,
        });
    };

    showNextImage = event => {
        const originalEvent = event.event || event;
        originalEvent.preventDefault();
        let currentImageIndex = this.state.currentImageIndex;
        currentImageIndex++;

        if (currentImageIndex > this.props.product.thumbnails.length - 1) {
            currentImageIndex = 0;
        }
        this.setState({
            currentImageIndex,
            isSwipeActive: true,
        });
    };

    sendToGTM = () => {
        const {
            pushToGTM,
            dataPosition,
            product,
            categoryId,
            hasSearchEngine,
        } = this.props;

        const merchandizingBanner = getProductBanner(
            product,
            categoryId,
            hasSearchEngine,
        );

        pushToGTM({
            article_id: product.article_id,
            event: 'click_on_product_card',
            event_trigger: 'product listing',
            is_b2b:
                product.model_markets && product.model_markets.includes('b2b'),
            is_mf: product.is_mmf,
            is_productoftheweek: merchandizingBanner === 'productOfTheWeek',
            is_recommended: merchandizingBanner === 'recommended',
            is_topsale: merchandizingBanner === 'topSales',
            model_id: product.model_id,
            product_card_position_absolute: dataPosition,
            product_id_algolia: parseInt(product.objectID, 10),
            product_price: product.price,
        });
    };

    render() {
        const {
            product,
            className,
            lazyloadImage,
            categoryLevel,
            showIconWishlist,
            isInWishList,
            openWishlistModal,
            promoted,
            experiences,
            baseUrls,
            isSalePeriod,
            cdn,
            platform,
            hasB2bCardStyle,
            hasB2bProRibbon,
            hasB2b,
            translations,
            categoryId,
            hasSearchEngine,
            user,
        } = this.props;

        const thumbnailCurrentImage = getCurrentThumbnail({
            product,
            CDN: cdn.CDN,
            index: this.state.currentImageIndex,
        });

        const merchandizingBanner = getProductBanner(
            product,
            categoryId,
            hasSearchEngine,
        );

        const isProProduct =
            product.has_pro_conditions && product.market === 'b2b' && hasB2b;

        const rootClassnames = classnames('product-list__product', className, {
            [`product-list__product-${merchandizingBanner}`]: merchandizingBanner,
            'product-list__product--promoted': promoted,
            'product-list-category__product': categoryLevel,
            'product-card--b2b': hasB2bCardStyle && isProProduct,
        });

        const CTAWrapper = promoted ? 'div' : Fragment;
        const ctaWrapperProps = {};
        if (promoted) {
            ctaWrapperProps.className =
                'product-list__product__link__cta-wrapper';
        }

        return (
            <ProductCard
                className={rootClassnames}
                product={product}
                onSwipeLeft={this.showNextImage}
                onSwipeRight={this.showPreviousImage}
                hasMerchandizing={!!merchandizingBanner}
            >
                {hasB2bProRibbon && isProProduct && <ProRibbon />}
                {merchandizingBanner && (
                    <Merchandizing
                        type={merchandizingBanner}
                        translations={translations}
                    />
                )}

                {showIconWishlist && (
                    <WishlistButton
                        className="product-card__icon-wishlist"
                        active={isInWishList}
                        product={product}
                        onClick={openWishlistModal}
                        isB2B={user.isB2B}
                    />
                )}

                <a
                    className="product-list__product__link"
                    href={`${baseUrls.main}${generateProductUrl(product)}`}
                    data-b="search-result"
                    title={product.title}
                    data-business-score={product.score}
                    data-is-recommended={isBannerRecommended(product)}
                    onClick={this.sendToGTM}
                >
                    <ProductCardPicture
                        className="product-list__product__link__picture"
                        product={product}
                        lazyload={!this.state.isSwipeActive && lazyloadImage}
                        thumbnailCurrentImage={thumbnailCurrentImage}
                        activePosition={this.state.currentImageIndex}
                        onSwipeLeft={this.showNextImage}
                        onSwipeRight={this.showPreviousImage}
                        isSalesPeriod={isSalePeriod}
                        paintExperience={experiences.includes(
                            categoryUtils.EXPERIENCES.PAINT,
                        )}
                        cdn={cdn}
                        platform={platform}
                    />
                    <div className="product-list__product__link__content-cta-wrapper">
                        <CTAWrapper {...ctaWrapperProps}>
                            <ProductCardContent product={product} cdn={cdn} />
                        </CTAWrapper>
                    </div>
                </a>
            </ProductCard>
        );
    }
}

export default compose(
    withFeatureFlags({
        hasSearchEngine: 'relevance.search.engine',
        hasB2bCardStyle: 'ff.browsing.b2b.listing.card.style',
        hasB2bProRibbon: 'ff.browsing.b2b.listing.pro.ribbon',
        hasB2b: 'ff.global.b2b',
    }),
    withWishList,
    withBaseUrls,
    withSales,
    withEnvironmentData,
    withGTM,
    withUser,
    withTranscriber('page_product', ['banner_fulfillment_title']),
)(Product);
